/**
 * 🏢 Enterprise Quota Gauge — Vague 4 P1
 * =======================================
 *
 * Composant premium réutilisable pour afficher le quota d'un forfait entreprise.
 * - Barre de progression avec palier de couleur (vert → ambre → rouge)
 * - Badge deadline avec rouge pulsant si ≤ 7j
 * - Informations textuelles claires : X / Y sessions · Z restantes · valable jusqu'au DD/MM
 *
 * Usage :
 *   <EnterpriseQuotaGauge
 *     allocated={10} used={8} remaining={2}
 *     validUntil="2026-06-30" companyName="AcmeCorp"
 *     orderType="human" onBook={() => navigate('/marketplace')}
 *   />
 */
import React, { useMemo } from 'react';
import { Calendar, Briefcase, ArrowRight, AlertCircle } from 'lucide-react';

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso).getTime();
    return Math.ceil((d - Date.now()) / 86400000);
  } catch { return null; }
}

function formatDateFR(iso) {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return iso; }
}

export default function EnterpriseQuotaGauge({
  allocated = 0, used = 0, remaining = 0,
  validUntil = null, companyName = null, orderType = null,
  eligibleCoachCount = 0, onBook,
}) {
  const pct = allocated > 0 ? Math.min(100, Math.round((used / allocated) * 100)) : 0;
  const remainingDays = daysUntil(validUntil);
  const urgentDeadline = remainingDays != null && remainingDays >= 0 && remainingDays <= 7;

  const barColor = useMemo(() => {
    if (pct >= 80) return 'bg-[#991B1B]';
    if (pct >= 50) return 'bg-[#D97706]';
    return 'bg-[#047857]';
  }, [pct]);

  const statusBadge = useMemo(() => {
    if (pct >= 80) return { label: 'Quota critique', cls: 'bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]' };
    if (pct >= 50) return { label: 'À mi-parcours', cls: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' };
    return { label: 'Sessions disponibles', cls: 'bg-[#D1FAE5] text-[#047857] border-[#A7F3D0]' };
  }, [pct]);

  return (
    <article className="bg-white border border-[#E8DFC8] rounded-2xl p-5 sm:p-6 shadow-sm"
      data-testid="enterprise-quota-gauge">
      <header className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#92710A] font-semibold mb-1">
            <Briefcase className="w-3.5 h-3.5" />
            {orderType === 'ia' ? 'Accompagnement IA' : 'Coaching humain'}
          </div>
          <h3 className="font-display text-[20px] text-[#111827] leading-tight truncate">
            {companyName || 'Forfait entreprise'}
          </h3>
          {eligibleCoachCount > 0 && (
            <p className="text-[12px] text-[#4B5563] mt-1">
              {eligibleCoachCount} coach{eligibleCoachCount > 1 ? 's' : ''} autorisé{eligibleCoachCount > 1 ? 's' : ''}
            </p>
          )}
        </div>
        <span className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold whitespace-nowrap ${statusBadge.cls}`}
          data-testid="gauge-status-badge">
          {statusBadge.label}
        </span>
      </header>

      {/* Bar */}
      <div className="mb-2">
        <div className="flex items-baseline justify-between mb-1.5">
          <div>
            <span className="font-display text-3xl text-[#111827] font-semibold" data-testid="gauge-used">{used}</span>
            <span className="lt-muted text-[13px] font-body"> / {allocated} sessions consommées</span>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl text-[#047857] font-semibold" data-testid="gauge-remaining">{remaining}</div>
            <div className="text-[11px] lt-muted -mt-0.5">restante{remaining > 1 ? 's' : ''}</div>
          </div>
        </div>
        <div className="w-full h-2.5 bg-[#F3F1EB] rounded-full overflow-hidden">
          <div
            className={`h-full ${barColor} transition-all duration-700 ease-out`}
            style={{ width: `${pct}%` }}
            data-testid="gauge-bar-fill"
          />
        </div>
        <div className="text-[11px] text-[#6B7280] mt-1">{pct}% du forfait utilisé</div>
      </div>

      {/* Deadline */}
      {validUntil && (
        <div className={`mt-4 flex items-center gap-2 text-[13px] px-3 py-2 rounded-lg border ${
          urgentDeadline ? 'bg-[#FEE2E2] text-[#991B1B] border-[#FECACA] animate-pulse' : 'bg-[#FBF9F3] text-[#4B5563] border-[#E8DFC8]'
        }`} data-testid="gauge-deadline">
          {urgentDeadline ? <AlertCircle className="w-4 h-4 flex-shrink-0" /> : <Calendar className="w-4 h-4 flex-shrink-0 text-[#92710A]" />}
          <span>
            {urgentDeadline
              ? <><strong>Plus que {remainingDays}j</strong> avant expiration · {formatDateFR(validUntil)}</>
              : <>Valable jusqu’au <strong>{formatDateFR(validUntil)}</strong>{remainingDays != null ? ` · ${remainingDays}j restants` : ''}</>}
          </span>
        </div>
      )}

      {/* CTA */}
      {onBook && remaining > 0 && (
        <button onClick={onBook}
          data-testid="gauge-cta-book"
          className="mt-4 w-full px-4 py-2.5 rounded-full bg-[#D4AF37] text-[#111827] text-sm font-semibold hover:bg-[#E5C15A] inline-flex items-center justify-center gap-2 transition-colors">
          Réserver une session
          <ArrowRight className="w-4 h-4" />
        </button>
      )}

      {remaining === 0 && (
        <div className="mt-4 text-center py-3 px-4 rounded-lg bg-[#F3F4F6] text-[13px] text-[#4B5563]"
          data-testid="gauge-quota-empty">
          Forfait entièrement consommé. Contactez votre service RH pour un renouvellement.
        </div>
      )}
    </article>
  );
}
